import { useAppSelector } from 'tradera/state/hooks';
import {
    selectAutomaticTranslationPreferred,
    selectPreferredLanguageCode
} from 'tradera/state/language/selectors';
import { selectMemberCountryCodeIso2 } from 'tradera/state/member/selectors';
import {
    useGetIndependentSearchResultQuery,
    useGetCategoryResultQuery
} from 'tradera/state/services/discover-api';
import {
    createCategoryPageApiRequest,
    createIndependentSearchApiRequest
} from 'tradera/features/discover/data';
import { QueryType } from 'tradera/state/views/start-page/item-queries';
import { selectTrendingSearches } from 'tradera/state/services/selectors/discover-api-selectors';
import type { TrendingSearchViewModel } from 'tradera/state/services/types/webapi-discover-generated';

const getTendingSearchesQuery = (
    trendingSearches: TrendingSearchViewModel[]
): string => {
    return trendingSearches.map((item) => item.query).join('+');
};

export const useGetItemList = (
    queryParams: Record<string, string>,
    queryType: QueryType,
    loadingTriggerInView: boolean
) => {
    const preferredLanguage = useAppSelector(selectPreferredLanguageCode);
    const countryCodeIso2 = useAppSelector(selectMemberCountryCodeIso2);
    const automaticTranslationPreferred = useAppSelector(
        selectAutomaticTranslationPreferred
    );

    const trendingSearches = useAppSelector(selectTrendingSearches());

    const trendingSearchesQuery = getTendingSearchesQuery(trendingSearches);

    const searchQueryParams =
        queryType === QueryType.Popular
            ? {
                  ...queryParams,
                  q: trendingSearchesQuery
              }
            : queryParams;

    const searchApiRequest = createIndependentSearchApiRequest(
        countryCodeIso2,
        automaticTranslationPreferred,
        preferredLanguage,
        searchQueryParams
    );

    const categoryApiRequest = createCategoryPageApiRequest(
        countryCodeIso2,
        automaticTranslationPreferred,
        preferredLanguage,
        queryParams
    );

    const skipSearchQuery =
        !loadingTriggerInView ||
        queryType === QueryType.Category ||
        !trendingSearchesQuery;

    const skipCategoryQuery =
        !loadingTriggerInView ||
        queryType !== QueryType.Category ||
        !queryParams.categoryId;

    const {
        data: searchData,
        isSuccess: searchIsSuccess,
        isLoading: searchIsLoading
    } = useGetIndependentSearchResultQuery(searchApiRequest, {
        skip: skipSearchQuery
    });

    const {
        data: categoryData,
        isSuccess: categoryIsSuccess,
        isLoading: categoryIsLoading
    } = useGetCategoryResultQuery(categoryApiRequest, {
        skip: skipCategoryQuery
    });

    const data = queryType === QueryType.Category ? categoryData : searchData;
    const isSuccess =
        queryType === QueryType.Category ? categoryIsSuccess : searchIsSuccess;
    const isLoading =
        queryType === QueryType.Category ? categoryIsLoading : searchIsLoading;

    return {
        data,
        isSuccess,
        isLoading
    };
};
